/**
 * The arithmetic of ordering a pool, kept out of the screen so it can be tested. A panelist's
 * list is an array of member ids in the order they chose.
 */

/** One place up (-1) or down (+1). The ends stay where they are; the input is not changed. */
export function moveBy<T>(items: readonly T[], from: number, delta: -1 | 1): T[] {
  const to = from + delta;
  if (from < 0 || from >= items.length || to < 0 || to >= items.length) return [...items];

  const next = [...items];
  [next[from], next[to]] = [next[to] as T, next[from] as T];
  return next;
}

/**
 * A panelist's list part-way through: the players ranked, in order; the players skipped; and the
 * pool of players not placed yet. The pool and the skipped are kept in the neutral order the
 * screen was given, so a player who goes back is where they were and nothing reshuffles.
 */
export interface Groups {
  ranked: string[];
  skipped: string[];
  pool: string[];
}

/** `items` with `id` added, in the order `neutral` gives. */
function inNeutralOrder(
  items: readonly string[],
  id: string,
  neutral: readonly string[],
): string[] {
  const keep = new Set([...items, id]);
  return neutral.filter((x) => keep.has(x));
}

/**
 * Rank a player from the pool at index `at` of the ranked list (from 0), the rest moving down;
 * at the end when `at` is left out. A player not in the pool leaves the groups as they were.
 */
export function place(groups: Groups, id: string, at?: number): Groups {
  if (!groups.pool.includes(id)) return groups;

  const index = Math.min(Math.max(at ?? groups.ranked.length, 0), groups.ranked.length);
  const ranked = [...groups.ranked];
  ranked.splice(index, 0, id);
  return { ranked, skipped: groups.skipped, pool: groups.pool.filter((x) => x !== id) };
}

/** Send a ranked player back to the pool. The players below them move up. */
export function unrank(groups: Groups, id: string, neutral: readonly string[]): Groups {
  if (!groups.ranked.includes(id)) return groups;

  return {
    ranked: groups.ranked.filter((x) => x !== id),
    skipped: groups.skipped,
    pool: inNeutralOrder(groups.pool, id, neutral),
  };
}

/** Skip a player in the pool: they count for nothing in this list. */
export function skip(groups: Groups, id: string, neutral: readonly string[]): Groups {
  if (!groups.pool.includes(id)) return groups;

  return {
    ranked: groups.ranked,
    skipped: inNeutralOrder(groups.skipped, id, neutral),
    pool: groups.pool.filter((x) => x !== id),
  };
}

/** Bring a skipped player back to the pool. */
export function unskip(groups: Groups, id: string, neutral: readonly string[]): Groups {
  if (!groups.skipped.includes(id)) return groups;

  return {
    ranked: groups.ranked,
    skipped: groups.skipped.filter((x) => x !== id),
    pool: inNeutralOrder(groups.pool, id, neutral),
  };
}

/**
 * A saved order brought up to date with who is registered now: anybody who has withdrawn is
 * dropped, and anybody new goes last, in the order `registered` gives them. This is what
 * `my_seeding` does in the database; the screen needs it too while the panelist is part-way
 * through arranging, before anything is saved.
 */
export function mergeOrder(saved: readonly string[], registered: readonly string[]): string[] {
  const here = new Set(registered);
  const kept = saved.filter((id) => here.has(id));
  const seen = new Set(kept);
  return [...kept, ...registered.filter((id) => !seen.has(id))];
}

/**
 * Whether a list no longer covers the pool: somebody registered is missing from it, or it holds
 * somebody who is not registered any more. Only a submitted list is ever out of date; a draft is
 * not counted in the first place.
 */
export function isOutOfDate(list: readonly string[], registered: readonly string[]): boolean {
  if (list.length !== registered.length) return true;
  const here = new Set(registered);
  return list.some((id) => !here.has(id));
}
